import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  alpha,
  Box,
  Card,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import type { GreeningQuestionItem } from '../../services/greeningApi';
import {
  GREENING_AGENT_LABEL,
  GREENING_AGENT_PHRASE,
} from './constants';
import { GREENING_BRAND } from './greeningFabActions';
import { GreeningBrandLabel, GreeningChatTitle } from './GreeningBrandLabel';
import { GreeningIcon } from './GreeningIcon';
import { GreeningBrandText } from './GreeningBrandText';
import { GreeningProcessingStatus } from './GreeningProcessingStatus';
import { GreeningResponseFooter } from './GreeningResponseFooter';
import { greeningReadingShade, scrollReadingTarget } from './greeningReadingMark';
import { visibleChatText } from './voice/browserSpeech';
import { sameSpokenQuestion, VoiceChatReveal } from './voice/voiceChatReveal';

interface Props {
  items: GreeningQuestionItem[];
  loading?: boolean;
  onAsk: (question: string) => Promise<void>;
  asking?: boolean;
  disabled?: boolean;
  /** Mensagem quando o input está bloqueado (ex.: aprofundamento em andamento). */
  disabledHint?: string;
  maxHeight?: number;
  /** Dentro de `GreeningChatDialog` — sem card/título externo. */
  embedded?: boolean;
  /** Escrita da resposta falada, linha a linha, enquanto a voz espera. */
  voiceReveal?: VoiceChatReveal | null;
}

function revealFor(question: string, reveal?: VoiceChatReveal | null): VoiceChatReveal | null {
  return reveal && sameSpokenQuestion(reveal.question, question) ? reveal : null;
}

function isPartialReveal(question: string, reveal?: VoiceChatReveal | null): boolean {
  const match = revealFor(question, reveal);
  return Boolean(match && match.lines.length > 0 && match.shown < match.lines.length);
}

function revealedAnswer(question: string, fallback: string, reveal?: VoiceChatReveal | null): string {
  const match = revealFor(question, reveal);
  if (!match || !match.lines.length) return fallback;
  const written = match.lines.slice(0, match.shown).join('\n');
  if (match.shown < match.lines.length) return written;
  return fallback || written;
}

function AnswerBubble({
  text,
  partial,
  showOaasCta,
  reading,
}: {
  text: string;
  partial?: boolean;
  showOaasCta?: boolean;
  reading?: VoiceChatReveal | null;
}) {
  if (!text.trim()) return null;
  return (
    <Box display="flex" justifyContent="flex-start" gap={1}>
      <GreeningIcon sx={{ fontSize: 22, color: 'success.main', mt: 0.5, flexShrink: 0 }} />
      <Box
        sx={(theme) => ({
          maxWidth: '88%',
          px: 1.5,
          py: 1,
          borderRadius: 2,
          borderTopLeftRadius: 4,
          bgcolor: alpha(theme.palette.success.main, 0.1),
          border: `1px solid ${alpha(theme.palette.success.main, 0.22)}`,
        })}
      >
        <Typography variant="caption" display="block" mb={0.5}>
          <GreeningBrandLabel fontWeight={600} />
        </Typography>
        <Box>
          {reading ? (
            reading.lines.slice(0, reading.shown).map((line, index) => {
              const active = index === reading.activeIndex && reading.activeIndex >= 0;
              return (
                <Typography
                  key={`${index}-${line.slice(0, 24)}`}
                  id={active ? 'greening-reading-line' : undefined}
                  variant="body2"
                  sx={(theme) => ({
                    px: 0.5,
                    ...greeningReadingShade(theme, active),
                  })}
                >
                  {line}
                </Typography>
              );
            })
          ) : (
            <GreeningBrandText text={visibleChatText(text)} />
          )}
        </Box>
        {!partial && <GreeningResponseFooter compact showOaasCta={showOaasCta} />}
      </Box>
    </Box>
  );
}

export function GreeningChatPanel({
  items,
  loading,
  onAsk,
  asking,
  disabled,
  disabledHint,
  maxHeight = 360,
  embedded = false,
  voiceReveal = null,
}: Props) {
  const theme = useTheme();
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const chronological = useMemo(
    () => [...items].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || '')),
    [items],
  );

  const hasPendingInList = chronological.some(
    (item) => item.status === 'pending' || item.status === 'processing' || item.status === 'retrying',
  );
  const showAskingFooter = Boolean(asking) && !hasPendingInList;

  useEffect(() => {
    const following = voiceReveal != null && (voiceReveal.activeIndex ?? -1) >= 0 && voiceReveal.lines.length > 0;
    if (!following) {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
      return undefined;
    }
    let attempts = 0;
    let timer = 0;
    const tick = () => {
      if (document.getElementById('greening-reading-line')) {
        scrollReadingTarget('greening-reading-line');
        return;
      }
      attempts += 1;
      if (attempts < 12) timer = window.setTimeout(tick, 80);
    };
    timer = window.setTimeout(tick, 0);
    return () => window.clearTimeout(timer);
  }, [chronological.length, asking, voiceReveal?.shown, voiceReveal?.question, voiceReveal?.activeIndex, voiceReveal?.lines.length]);

  const handleSubmit = async () => {
    const text = draft.trim();
    if (!text || disabled || asking) return;
    await onAsk(text);
    setDraft('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i += 1) {
      if (items[i].type.startsWith('image/')) {
        e.preventDefault();
        return;
      }
    }
    const text = e.clipboardData.getData('text/plain');
    if (/^data:(image|application)\//i.test(text) || /[A-Za-z0-9+/]{200,}={0,2}/.test(text)) {
      e.preventDefault();
    }
  };

  const messagesArea = (
    <Box
      ref={scrollRef}
      sx={{
        px: 2,
        py: 1.5,
        ...(embedded
          ? { flex: 1, minHeight: 0, overflowY: 'auto' }
          : { maxHeight, overflowY: 'auto' }),
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        bgcolor: alpha(theme.palette.grey[100], embedded ? 0.35 : 0.5),
      }}
    >
      {loading && !chronological.length && (
        <Typography variant="body2" color="text.secondary">
          Carregando conversa…
        </Typography>
      )}
      {!loading && !chronological.length && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          {disabled
            ? disabledHint || `Aguarde a conclusão da análise para conversar com ${GREENING_AGENT_PHRASE}.`
            : 'Faça sua primeira pergunta sobre esta legislação.'}
        </Typography>
      )}
      {chronological.map((item) => (
        <Box key={item.id}>
          <Box display="flex" justifyContent="flex-end" mb={0.75}>
            <Box
              sx={{
                maxWidth: '88%',
                px: 1.5,
                py: 1,
                borderRadius: 2,
                borderTopRightRadius: 4,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Typography variant="body2">{item.question}</Typography>
            </Box>
          </Box>
          {(item.status === 'pending' ||
            item.status === 'processing' ||
            item.status === 'retrying') &&
            !item.answer && (
            <Box display="flex" alignItems="center" gap={1} pl={0.5}>
              <GreeningIcon sx={{ fontSize: 20, color: 'success.main' }} />
              <GreeningProcessingStatus status={item.status} compact />
            </Box>
          )}
          {item.status === 'pending' && item.answer && (
            <AnswerBubble text={item.answer} />
          )}
          {item.status === 'blocked_by_security' && item.answer && (
            <AnswerBubble text={item.answer} />
          )}
          {item.status === 'insufficient_context' && item.answer && (
            <AnswerBubble text={item.answer} />
          )}
          {(item.status === 'failed' ||
            (item.status === 'blocked_by_security' && !item.answer) ||
            (item.status === 'insufficient_context' && !item.answer)) && (
            <Box display="flex" justifyContent="flex-start" gap={1}>
              <GreeningIcon sx={{ fontSize: 22, color: 'success.main', mt: 0.5, flexShrink: 0 }} />
              <Box
                sx={{
                  maxWidth: '88%',
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  borderTopLeftRadius: 4,
                  bgcolor: alpha(theme.palette.error.main, 0.06),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
                }}
              >
                <Typography variant="caption" display="block" mb={0.5}>
                  <GreeningBrandLabel fontWeight={600} />
                </Typography>
                <GreeningProcessingStatus status={item.status} compact />
              </Box>
            </Box>
          )}
          {(item.status === 'rate_limited' || item.rate_limited) && item.answer && (
            <Box display="flex" justifyContent="flex-start" gap={1}>
              <GreeningIcon sx={{ fontSize: 22, color: 'success.main', mt: 0.5, flexShrink: 0 }} />
              <Box
                sx={{
                  maxWidth: '88%',
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  borderTopLeftRadius: 4,
                  bgcolor: alpha(GREENING_BRAND.teal, 0.08),
                  border: `1px solid ${alpha(GREENING_BRAND.teal, 0.28)}`,
                }}
              >
                <Typography variant="caption" display="block" mb={0.25}>
                  <GreeningBrandLabel fontWeight={600} />
                </Typography>
                <Typography variant="caption" color="warning.dark" fontWeight={600} display="block" mb={0.5}>
                  Limite de uso do contrato
                </Typography>
                <GreeningBrandText text={item.answer} linkifyEmail />
              </Box>
            </Box>
          )}
          {item.status === 'completed' && item.answer && !item.rate_limited && (
            <AnswerBubble
              text={revealedAnswer(item.question, item.answer, voiceReveal)}
              partial={isPartialReveal(item.question, voiceReveal)}
              showOaasCta={item.show_oaas_cta && !item.scope_redirect}
              reading={revealFor(item.question, voiceReveal)}
            />
          )}
          {item.status !== 'completed' && revealedAnswer(item.question, '', voiceReveal) && (
            <AnswerBubble
              text={revealedAnswer(item.question, '', voiceReveal)}
              partial
              reading={revealFor(item.question, voiceReveal)}
            />
          )}
        </Box>
      ))}
      {voiceReveal && !chronological.some((item) => sameSpokenQuestion(item.question, voiceReveal.question)) && (
        <Box>
          <Box display="flex" justifyContent="flex-end" mb={0.75}>
            <Box
              sx={{
                maxWidth: '88%',
                px: 1.5,
                py: 1,
                borderRadius: 2,
                borderTopRightRadius: 4,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Typography variant="body2">{voiceReveal.question}</Typography>
            </Box>
          </Box>
          <AnswerBubble
            text={voiceReveal.lines.slice(0, voiceReveal.shown).join('\n')}
            partial={voiceReveal.shown < voiceReveal.lines.length}
            reading={voiceReveal}
          />
        </Box>
      )}
      {showAskingFooter && (
        <Box display="flex" alignItems="center" gap={1} pl={0.5}>
          <GreeningIcon sx={{ fontSize: 20, color: 'success.main' }} />
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            O agente <GreeningBrandLabel fontWeight={600} /> está elaborando a resposta…
          </Typography>
        </Box>
      )}
    </Box>
  );

  const inputArea = (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        pb: embedded ? 1.5 : 2,
        flexShrink: 0,
        borderTop: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        gap: 1,
        alignItems: 'flex-end',
      }}
    >
      <TextField
        fullWidth
        multiline
        maxRows={4}
        minRows={1}
        size="small"
        placeholder={`Pergunte ao agente ${GREENING_AGENT_LABEL} sobre esta norma…`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        disabled={disabled || asking}
      />
      <IconButton
        color="success"
        onClick={() => void handleSubmit()}
        disabled={disabled || asking || !draft.trim()}
        sx={{
          bgcolor: alpha(theme.palette.success.main, 0.12),
          '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.2) },
        }}
        aria-label="Enviar pergunta"
      >
        <SendIcon />
      </IconButton>
    </Box>
  );

  if (embedded) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          height: '100%',
          maxHeight: maxHeight,
        }}
      >
        {messagesArea}
        {inputArea}
      </Box>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        mt: 2,
        borderColor: alpha(theme.palette.primary.main, 0.25),
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="subtitle2" fontWeight={700}>
          <GreeningChatTitle variant="subtitle1" />
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Suas perguntas sobre esta norma — respostas do agente de legislação ambiental.
        </Typography>
      </Box>
      {messagesArea}
      {inputArea}
    </Card>
  );
}

export default GreeningChatPanel;
